import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { AppEvent } from '../store/eventsSlice'
import { updateEvent as updateEventSlice } from '../store/eventsSlice'
import { useAppDispatch } from '../store/hooks'
import { isRegistrationOpen } from '../utils/event'
import { formatLocalDateTime } from '../utils/datetime'
import { toUpdateEventBody } from '../pages/event/eventMapping'
import { Table } from './Table'
import { TablePagination } from './TablePagination'
import { Button } from './Button'
import { usePagedParticipants } from '../hooks/usePagedParticipants'
import { InviteModal } from './InviteModal'
import { GenerateReportButton } from './GenerateReportButton'
import { ConfirmationModal } from './ConfirmationModal'
import { ParticipantConversationModal } from './ParticipantConversationModal'
import { intersectEligibleIds } from '../utils/bulkActions'
import {
  useInviteParticipantMutation,
  useResendEventParticipantInvitationMutation,
  useRemoveParticipantMutation,
  useBatchUpdateParticipantsMutation,
  useUpdateEventMutation,
  type ParticipantSummary,
  type ExtendedInviteBody,
} from '@proaxiv/server-api'

// ── Icons ─────────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const SendIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
)

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const ClockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
)

// ── Row action icon button ────────────────────────────────────────────────────
//
// Small square icon button used in the desktop table's "Actions" column, matching the strip
// used on the Accepted Participants tab (`AcceptedRowActions`) so every participant table
// exposes per-row actions the same way, in addition to the bulk toolbar above.

function RowActionButton({
  icon,
  label,
  onClick,
  colorClass,
}: {
  icon: ReactNode
  label: string
  onClick: () => void
  colorClass: string
}) {
  return (
    <Button
      type="button"
      variant="unstyled"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={[
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-150',
        'focus-visible:ring-pax-blue/40 focus-visible:ring-2 focus-visible:outline-none',
        'active:scale-90',
        colorClass,
      ].join(' ')}
    >
      {icon}
    </Button>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  event: AppEvent
}

type ConfirmAction = { type: 'delete' | 'resend' | 'singleDelete'; ids: string[] }

/** Invited Participants tab content inside the EventManagementPage. */
export function InvitedParticipantsTab({ event }: Props) {
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()

  const {
    participants: invitations,
    total,
    isLoading,
    isError,
    offset,
    take,
    sort,
    order,
    setOffset,
    setTake,
    toggleSort,
  } = usePagedParticipants({
    eventId: event.id,
    status: 'INVITED',
    defaultSort: 'lastInvitedAt',
    defaultOrder: 'asc',
  })

  const [inviteParticipant, { isLoading: inviting }] = useInviteParticipantMutation()
  const [resendInvitation] = useResendEventParticipantInvitationMutation()
  const [removeParticipant] = useRemoveParticipantMutation()
  const [batchUpdate] = useBatchUpdateParticipantsMutation()
  const [updateEvent, { isLoading: publishing }] = useUpdateEventMutation()

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [isSendingInvites, setIsSendingInvites] = useState(false)
  const [inviteSuccessCount, setInviteSuccessCount] = useState<number | null>(null)
  const inviteSuccessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [publishError, setPublishError] = useState<string | null>(null)
  const [openRegError, setOpenRegError] = useState<string | null>(null)
  const [conversationFor, setConversationFor] = useState<{ id: string; name: string } | null>(null)

  useEffect(() => {
    return () => {
      if (inviteSuccessTimerRef.current) clearTimeout(inviteSuccessTimerRef.current)
    }
  }, [])

  const regOpen = isRegistrationOpen(event)
  const isDraft = event.status !== 'published'
  const canInvite = regOpen && !isDraft
  const selectedCount = selectedKeys.size
  const selectedInvites = invitations.filter((i) => selectedKeys.has(i.id!))
  // Same intersection pattern as every other participant tab's bulk toolbar (see
  // `intersectEligibleIds`): both actions apply to every invited row unconditionally today, but
  // routing them through the shared helper keeps this tab consistent with the others and ready
  // for a future per-row eligibility rule (e.g. excluding rows invited moments ago).
  const bulkResendIds = intersectEligibleIds(
    selectedInvites,
    (i) => i.id!,
    () => true
  )
  const bulkDeleteIds = bulkResendIds

  // ── Selection handlers ───────────────────────────────────────────────────────

  function handleSelectRow(key: string, checked: boolean) {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  function handleSelectAll(checked: boolean) {
    setSelectedKeys(checked ? new Set(invitations.map((i) => i.id!)) : new Set())
  }

  // ── Invite modal ─────────────────────────────────────────────────────────────

  async function handleInviteConfirm(
    entries: { email: string; customMessage?: string; language: string }[]
  ) {
    setInviteError(null)
    setIsSendingInvites(true)
    try {
      for (const entry of entries) {
        await inviteParticipant({
          eventId: event.id,
          inviteBody: {
            email: entry.email,
            role: 'AUDIENCE',
            customMessage: entry.customMessage,
            language: entry.language,
          } as ExtendedInviteBody,
        }).unwrap()
      }
      setShowInviteModal(false)
      setInviteSuccessCount(entries.length)
      if (inviteSuccessTimerRef.current) clearTimeout(inviteSuccessTimerRef.current)
      inviteSuccessTimerRef.current = setTimeout(() => {
        setInviteSuccessCount(null)
      }, 5000)
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status
      const data = (err as { data?: { errorCode?: string; message?: string } })?.data
      if (status === 409 && data?.errorCode === 'REGISTRATION_CLOSED') {
        setInviteError(t('invite_modal_error_reg_closed'))
      } else if (status === 409) {
        setInviteError(data?.message ?? t('invite_modal_error_duplicate'))
      } else {
        setInviteError(t('invite_modal_error_generic'))
      }
    } finally {
      setIsSendingInvites(false)
    }
  }

  // ── Confirm actions (batch toolbar or single-row resend) ─────────────────────

  async function handleConfirmAction() {
    if (!confirmAction) return
    const { type, ids } = confirmAction
    if (type === 'delete') {
      await batchUpdate({
        eventId: event.id,
        batchBody: {
          participantIds: ids,
          action: 'DELETE',
          sendEmailNotification: false,
        },
      })
    } else if (type === 'resend') {
      for (const id of ids) {
        await resendInvitation({ participantId: id })
      }
    } else if (type === 'singleDelete') {
      await removeParticipant({ participantId: ids[0] })
    }
    setSelectedKeys(new Set())
    setConfirmAction(null)
  }

  // ── Publish ──────────────────────────────────────────────────────────────────

  async function handlePublish() {
    setPublishError(null)
    // eventMapping's payload type omits id/createdAt — strip them explicitly rather than
    // spreading the whole event so TS enforces the rest matches the expected shape.
    const { id, createdAt, ...rest } = event
    void id
    void createdAt
    try {
      await updateEvent({
        eventId: event.id,
        updateEventBody: toUpdateEventBody({ ...rest, status: 'published' }),
      }).unwrap()
      // Keeps the local Redux store in sync immediately — the "Event" tag invalidation on the
      // mutation also triggers a refetch of `useGetEventQuery`, but that lands a beat later.
      dispatch(updateEventSlice({ id: event.id, status: 'published' }))
    } catch (err) {
      console.error('Failed to publish event', err)
      setPublishError(t('invited_publish_error'))
    }
  }

  // ── Open registration ────────────────────────────────────────────────────────

  async function handleOpenRegistration() {
    setOpenRegError(null)
    const { id, createdAt, ...rest } = event
    void id
    void createdAt
    try {
      await updateEvent({
        eventId: event.id,
        updateEventBody: toUpdateEventBody({ ...rest, registrationManualState: 'open' }),
      }).unwrap()
      dispatch(updateEventSlice({ id: event.id, registrationManualState: 'open' }))
    } catch (err) {
      console.error('Failed to open registration', err)
      setOpenRegError(t('invited_open_reg_error'))
    }
  }

  // ── Single-row actions ───────────────────────────────────────────────────────

  function requestSingleResend(id: string) {
    setConfirmAction({ type: 'resend', ids: [id] })
  }

  function requestSingleDelete(id: string) {
    setConfirmAction({ type: 'singleDelete', ids: [id] })
  }

  // ── Table columns ────────────────────────────────────────────────────────────

  const columns = [
    {
      header: t('invited_col_email'),
      headerTextTransform: 'none' as const,
      sortKey: 'email',
      render: (row: ParticipantSummary) => <span className="font-medium">{row.email}</span>,
    },
    {
      header: t('invited_col_last_invited'),
      headerTextTransform: 'none' as const,
      sortKey: 'lastInvitedAt',
      render: (row: ParticipantSummary) => (
        <span className="text-pax-gray text-xs tabular-nums">
          {row.lastInvitedAt ? formatLocalDateTime(row.lastInvitedAt, i18n.language) : '—'}
        </span>
      ),
    },
    {
      // Header left visually blank (#274) — the action icons are self-explanatory in context;
      // headerAriaLabel keeps the column named for screen readers/tests.
      header: '',
      headerAriaLabel: t('reg_col_actions'),
      headerTextTransform: 'none' as const,
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      render: (row: ParticipantSummary) => (
        <div className="flex items-center justify-end gap-0.5">
          <RowActionButton
            icon={<ClockIcon />}
            label={t('conversation_action')}
            onClick={() => setConversationFor({ id: row.id!, name: row.email ?? '' })}
            colorClass="text-pax-gray hover:bg-pax-purple/15"
          />
          <RowActionButton
            icon={<SendIcon />}
            label={t('invited_action_resend')}
            onClick={() => requestSingleResend(row.id!)}
            colorClass="text-pax-blue hover:bg-pax-blue/10"
          />
          <RowActionButton
            icon={<TrashIcon />}
            label={t('invited_action_delete')}
            onClick={() => requestSingleDelete(row.id!)}
            colorClass="text-pax-red hover:bg-pax-red/10"
          />
        </div>
      ),
    },
  ]

  // ── Loading / error states ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="border-pax-purple/20 animate-pulse rounded-2xl border bg-white p-12 text-center">
        <div className="bg-pax-purple/10 mx-auto h-4 w-32 rounded" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="border-pax-red/20 bg-pax-red/5 rounded-2xl border p-6 text-center text-sm text-red-500">
        {t('invited_load_error')}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {inviteSuccessCount != null && (
        <div
          role="status"
          className="bg-pax-green/8 border-pax-green/25 flex items-center gap-2 rounded-lg border px-3 py-2.5"
        >
          <p className="text-pax-black text-xs font-medium">
            {t('invite_modal_success', { count: inviteSuccessCount })}
          </p>
        </div>
      )}

      {isDraft ? (
        <div className="border-pax-orange-warm/30 bg-pax-orange-warm/8 flex flex-col gap-2 rounded-xl border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <span className="text-pax-orange-warm font-medium">{t('invited_draft_notice')}</span>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handlePublish()}
            disabled={publishing}
            className="w-full sm:w-auto"
          >
            {publishing ? t('btn_saving') : t('invited_publish_btn')}
          </Button>
          {publishError && (
            <p className="text-pax-red basis-full text-xs font-medium">{publishError}</p>
          )}
        </div>
      ) : (
        !regOpen && (
          <div className="border-pax-orange-warm/30 bg-pax-orange-warm/8 flex flex-col gap-2 rounded-xl border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
            <span className="text-pax-orange-warm font-medium">
              {t('invited_reg_closed_notice')}
            </span>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleOpenRegistration()}
              disabled={publishing}
              className="w-full sm:w-auto"
            >
              {publishing ? t('btn_saving') : t('invited_open_reg_btn')}
            </Button>
            {openRegError && (
              <p className="text-pax-red basis-full text-xs font-medium">{openRegError}</p>
            )}
          </div>
        )
      )}

      {/* Combined pager + table actions. `flex-wrap` on the row (and on the right-hand
          button group) lets the report/invite buttons drop to a second line on narrow phones
          instead of overflowing past the tab's right edge — see #228. */}
      <TablePagination
        total={total}
        offset={offset}
        take={take}
        onOffsetChange={setOffset}
        onTakeChange={setTake}
        toolbarTestId="invited-toolbar"
        selectionActions={
          selectedCount > 0 ? (
            <>
              <span className="text-pax-gray text-xs font-medium">
                {t('n_selected', { count: selectedCount })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<SendIcon />}
                onClick={() => setConfirmAction({ type: 'resend', ids: bulkResendIds })}
              >
                {t('invited_action_resend')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                leftIcon={<TrashIcon />}
                onClick={() => setConfirmAction({ type: 'delete', ids: bulkDeleteIds })}
              >
                {t('invited_action_delete')}
              </Button>
            </>
          ) : undefined
        }
        actions={
          <>
            <GenerateReportButton
              eventId={event.id!}
              reportType="invited"
              labelKey="report_generate_invited_pdf"
            />
            {canInvite && (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<PlusIcon />}
                onClick={() => {
                  setInviteError(null)
                  setShowInviteModal(true)
                }}
                disabled={inviting}
              >
                {t('invited_invite_btn')}
              </Button>
            )}
          </>
        }
      >
        <Table
          columns={columns}
          rows={invitations}
          keyExtractor={(row) => row.id!}
          emptyMessage={t('invited_empty')}
          selectable
          selectedKeys={selectedKeys}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
          rowAriaLabel={(row) => t('table_select_row', { name: row.email })}
          sortField={sort}
          sortOrder={order}
          onSort={toggleSort}
          renderCard={(inv, isSelected, onSelect) => (
            <div
              className={[
                'border-pax-purple/20 rounded-2xl border bg-white p-4 shadow-sm',
                isSelected ? 'bg-pax-light-blue/40' : '',
              ].join(' ')}
            >
              <div className="mb-3 flex items-start gap-3">
                <input
                  type="checkbox"
                  aria-label={t('table_select_row', { name: inv.email })}
                  checked={isSelected}
                  onChange={(e) => onSelect(e.target.checked)}
                  className="accent-pax-blue mt-0.5 h-4 w-4 cursor-pointer rounded"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-pax-black font-medium">{inv.email}</p>
                  {/* No status badge here — every row on this tab is, by definition, an invited
                    participant, so it would only ever repeat "Invitation sent". */}
                  <p className="text-pax-gray mt-1 text-[10px] tabular-nums">
                    {inv.lastInvitedAt
                      ? formatLocalDateTime(inv.lastInvitedAt, i18n.language)
                      : '—'}
                  </p>
                </div>
              </div>
              {/* `grid grid-cols-3` (not `flex` + `w-full`) — Tailwind's `grid-cols-N` expands to
                `repeat(3, minmax(0, 1fr))`, which explicitly floors each track's minimum width
                at 0. A plain `flex` row of three `w-full` buttons instead lets each item's
                default `min-width: auto` win, which is its *content*-based minimum (icon + the
                widest unbreakable run of text) — with three of these stacked in a ~250px mobile
                card, that combined minimum was wider than the card, so the row (and the Delete
                button specifically, being last) overflowed past the card's right edge instead of
                shrinking to fit. See #228. */}
              <div data-testid="invited-card-actions" className="grid grid-cols-3 gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  leftIcon={<ClockIcon />}
                  onClick={() => setConversationFor({ id: inv.id!, name: inv.email ?? '' })}
                >
                  {t('conversation_action')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  leftIcon={<SendIcon />}
                  onClick={() => requestSingleResend(inv.id!)}
                >
                  {t('invited_action_resend')}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  fullWidth
                  leftIcon={<TrashIcon />}
                  onClick={() => requestSingleDelete(inv.id!)}
                >
                  {t('invited_action_delete')}
                </Button>
              </div>
            </div>
          )}
        />
      </TablePagination>

      {showInviteModal && (
        <InviteModal
          error={inviteError}
          isSending={isSendingInvites}
          onConfirm={(entries) => void handleInviteConfirm(entries)}
          onCancel={() => {
            if (isSendingInvites) return
            setInviteError(null)
            setShowInviteModal(false)
          }}
        />
      )}

      {(confirmAction?.type === 'delete' || confirmAction?.type === 'singleDelete') && (
        <ConfirmationModal
          title={t('invited_delete_confirm', { count: confirmAction.ids.length })}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={() => void handleConfirmAction()}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {confirmAction?.type === 'resend' && (
        <ConfirmationModal
          title={t('invited_resend_confirm', { count: confirmAction.ids.length })}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={() => void handleConfirmAction()}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {conversationFor && (
        <ParticipantConversationModal
          participantId={conversationFor.id}
          participantName={conversationFor.name}
          onClose={() => setConversationFor(null)}
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<SendIcon />}
                onClick={() => {
                  const id = conversationFor.id
                  setConversationFor(null)
                  requestSingleResend(id)
                }}
              >
                {t('invited_action_resend')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                leftIcon={<TrashIcon />}
                onClick={() => {
                  const id = conversationFor.id
                  setConversationFor(null)
                  requestSingleDelete(id)
                }}
              >
                {t('invited_action_delete')}
              </Button>
            </>
          }
        />
      )}
    </div>
  )
}
